import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Search, Home, Heart, Shield, GraduationCap, Building2,
  Mail, HelpCircle, DollarSign, Users, BookOpen, Briefcase,
  X, ArrowRight, CornerDownLeft, ChevronUp, ChevronDown,
  Rocket, Zap, Globe, BarChart3
} from "lucide-react";

const COMMANDS = [
  {
    group: "Navigation",
    items: [
      { id: "home",         label: "Home",           desc: "Back to landing page",         icon: Home,         path: "/",            color: "text-gray-400"   },
      { id: "about",        label: "About Us",       desc: "Our story and mission",        icon: Rocket,       path: "/about",        color: "text-emerald-400"},
      { id: "pricing",      label: "Pricing",        desc: "Plans and pricing",            icon: DollarSign,   path: "/pricing",      color: "text-amber-400"  },
      { id: "contact",      label: "Contact",        desc: "Get in touch",                 icon: Mail,         path: "/contact",      color: "text-cyan-400"   },
      { id: "blog",         label: "Blog",           desc: "Articles and insights",        icon: BookOpen,     path: "/blog",         color: "text-blue-400"   },
      { id: "careers",      label: "Careers",        desc: "Join the team",                icon: Briefcase,    path: "/careers",      color: "text-purple-400" },
      { id: "faq",          label: "FAQ",            desc: "Frequently asked questions",   icon: HelpCircle,   path: "/faq",          color: "text-rose-400"   },
    ],
  },
  {
    group: "Products",
    items: [
      { id: "health-nexus", label: "Health Nexus AI", desc: "AI-powered healthcare platform", icon: Heart,       path: "/health-nexus", color: "text-emerald-400"},
      { id: "aegis-x",      label: "AEGIS-X Security",desc: "Military-grade cybersecurity",  icon: Shield,      path: "/aegis-x",      color: "text-purple-400" },
      { id: "academy",      label: "VitalMind Academy",desc: "AI & tech education",          icon: GraduationCap,path: "/academy",      color: "text-cyan-400"   },
      { id: "services",     label: "Enterprise IT",   desc: "Custom AI solutions",           icon: Building2,   path: "/services",     color: "text-blue-400"   },
    ],
  },
  {
    group: "Company",
    items: [
      { id: "investors",    label: "Investors",      desc: "Investment deck",              icon: BarChart3,    path: "/investors",    color: "text-amber-400"  },
      { id: "press",        label: "Press",          desc: "Media & news",                 icon: Globe,        path: "/press",        color: "text-gray-400"   },
      { id: "status",       label: "Platform Status",desc: "Uptime & incidents",           icon: Zap,          path: "/status",       color: "text-emerald-400"},
    ],
  },
];

const ALL_ITEMS = COMMANDS.flatMap(g => g.items.map(item => ({ ...item, group: g.group })));

export default function CommandPalette() {
  const [open, setOpen]     = useState(false);
  const [query, setQuery]   = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef            = useRef(null);
  const navigate            = useNavigate();

  /* ── open/close on Cmd+K or custom event ────────────────────────────── */
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(o => !o);
      }
      if (e.key === "Escape") setOpen(false);
    };
    const customOpen = () => setOpen(o => !o);
    window.addEventListener("keydown", handler);
    window.addEventListener("openCommandPalette", customOpen);
    return () => {
      window.removeEventListener("keydown", handler);
      window.removeEventListener("openCommandPalette", customOpen);
    };
  }, []);

  /* ── reset on open ────────────────────────────────────────────────────── */
  useEffect(() => {
    if (open) { setQuery(""); setCursor(0); setTimeout(() => inputRef.current?.focus(), 60); }
  }, [open]);

  /* ── filtered results ─────────────────────────────────────────────────── */
  const results = query.trim()
    ? ALL_ITEMS.filter(item =>
        item.label.toLowerCase().includes(query.toLowerCase()) ||
        item.desc.toLowerCase().includes(query.toLowerCase()) ||
        item.group.toLowerCase().includes(query.toLowerCase())
      )
    : null;

  const displayGroups = results
    ? [{ group: `Results for "${query}"`, items: results }]
    : COMMANDS;

  const flatItems = displayGroups.flatMap(g => g.items);

  /* ── keyboard navigation ──────────────────────────────────────────────── */
  const handleKey = useCallback((e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor(c => Math.min(c + 1, flatItems.length - 1)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
    if (e.key === "Enter" && flatItems[cursor]) {
      navigate(flatItems[cursor].path);
      setOpen(false);
    }
  }, [flatItems, cursor, navigate]);

  const go = useCallback((path) => { navigate(path); setOpen(false); }, [navigate]);

  let globalIdx = 0;

  return (
    <>
      {/* ── Overlay ───────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 z-[9998] bg-[#030712]/80 backdrop-blur-md"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              onClick={() => setOpen(false)}
            />

            {/* Panel */}
            <motion.div
              className="fixed top-[12vh] left-1/2 -translate-x-1/2 z-[9999] w-full max-w-xl px-4"
              initial={{ opacity: 0, y: -24, scale: 0.95 }}
              animate={{ opacity: 1, y: 0,  scale: 1 }}
              exit={{ opacity: 0,  y: -16, scale: 0.96 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="glass-card rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/60">

                {/* Search input */}
                <div className="flex items-center gap-3 px-4 py-3.5 border-b border-white/[0.07]">
                  <Search className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  <input
                    ref={inputRef}
                    value={query}
                    onChange={e => { setQuery(e.target.value); setCursor(0); }}
                    onKeyDown={handleKey}
                    placeholder="Search pages, products, or features..."
                    className="flex-1 bg-transparent text-white text-sm placeholder-gray-600 outline-none border-none"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  {query && (
                    <button onClick={() => setQuery("")} className="text-gray-600 hover:text-gray-400 transition-colors">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <kbd className="px-2 py-0.5 rounded bg-white/[0.05] border border-white/[0.08] text-[10px] font-mono text-gray-600">ESC</kbd>
                </div>

                {/* Results */}
                <div className="max-h-[380px] overflow-y-auto py-2">
                  {results?.length === 0 && (
                    <div className="py-10 text-center text-gray-600 text-sm">No results for "{query}"</div>
                  )}
                  {displayGroups.map((group) => (
                    <div key={group.group}>
                      <div className="px-4 py-2 text-[10px] uppercase tracking-widest text-gray-600 font-semibold">
                        {group.group}
                      </div>
                      {group.items.map((item) => {
                        const isActive = globalIdx === cursor;
                        const idx = globalIdx++;
                        return (
                          <button
                            key={item.id}
                            onClick={() => go(item.path)}
                            onMouseEnter={() => setCursor(idx)}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all ${
                              isActive ? "bg-white/[0.06]" : "hover:bg-white/[0.03]"
                            }`}
                          >
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              isActive ? "bg-emerald-500/15 border border-emerald-500/25" : "bg-white/[0.04] border border-white/[0.06]"
                            }`}>
                              <item.icon className={`w-4 h-4 ${isActive ? "text-emerald-400" : item.color}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-white truncate">{item.label}</div>
                              <div className="text-xs text-gray-500 truncate">{item.desc}</div>
                            </div>
                            {isActive && (
                              <CornerDownLeft className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <div className="px-4 py-2.5 border-t border-white/[0.05] flex items-center gap-4 text-[10px] text-gray-600">
                  <span className="flex items-center gap-1.5"><ChevronUp className="w-3 h-3" /><ChevronDown className="w-3 h-3" /> Navigate</span>
                  <span className="flex items-center gap-1.5"><CornerDownLeft className="w-3 h-3" /> Open</span>
                  <span className="flex items-center gap-1.5"><kbd className="font-mono">ESC</kbd> Close</span>
                  <div className="ml-auto flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-emerald-400 font-medium">VitalMind</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
